import { GameProvidersRequestDto } from '@/services/dto/game-providers/game-providers-request.dto';
import { UseGameProvidersProps, UseGameProvidersQueryKey } from './props';
import { GameProvidersQueryKeys } from '../../queryKeys';
import { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@starsoft/common/services/query-client';
import { AxiosError } from 'axios';
import { GenericError } from '@/models/generic-error';
import { fetchGamesProviders } from './service';
import { useMemo } from 'react';
import {
  useInfiniteQueryCallbacks,
  useInfiniteQueryReset,
} from '@starsoft/common/hooks';
import { PaginatedResponse } from '@starsoft/common/models';
import { GameProvider } from '@/models/game/provider';

export function useGameProviders(options?: UseGameProvidersProps) {
  const { enabled = true, limit = 20, query } = options ?? {};
  const params: GameProvidersRequestDto = {
    limit,
    query,
  };
  const queryKey: UseGameProvidersQueryKey = [
    GameProvidersQueryKeys.Primary,
    params,
  ];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();
  useInfiniteQueryReset(queryKey);

  const { data, isPending, ...infinityQuery } = useInfiniteQuery<
    PaginatedResponse<GameProvider>,
    AxiosError<GenericError>,
    InfiniteData<PaginatedResponse<GameProvider>>,
    UseGameProvidersQueryKey
  >({
    queryKey,
    queryFn: fetchGamesProviders,
    initialPageParam: 1,
    getNextPageParam,
    getPreviousPageParam,
    enabled,
    staleTime: 5 * 1000,
    refetchOnWindowFocus: false,
  });

  const formattedData = useMemo(
    () => data?.pages?.flatMap(page => page?.data),
    [data],
  );

  return {
    ...infinityQuery,
    isLoading: isPending,
    gameProviders: formattedData,
    metadata: data?.pages?.[0]?.metadata,
    error: infinityQuery.error as AxiosError<GenericError>,
  };
}
